import { CrownIcon, Paragraph, SendIcon, TrashCanIcon, notification } from "@datagame/ui";
import { sortBy } from "lodash";
import { useMediaQuery } from "react-responsive";
import { useMemo } from "react";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";

import { User, UserRole } from "../../../../gql/graphql";
import { TableConfig } from "../../../../types/table";
import { Table } from "../../../shared/ui/Table";
import { RoleBadge } from "../../../shared/ui/RoleBadge";
import { Dropdown } from "../../../shared/ui/Dropdown";
import {
  USER_DELETE,
  USER_GRANT_ADMIN,
  USER_RESEND_INVITATION,
  USER_REVOKE_ADMIN,
} from "../../../../graphql/mutations/UserMutations";
import { USER_LIST } from "../../../../graphql/queries/UserQueries";
import { isAdmin } from "../../../../utils/user/isAdmin";
import { DropdownOption } from "../../../shared/ui/Dropdown/Dropdown";

import styles from "./UserList.module.scss";

export interface UserListProps {
  users: User[];
  currentUser?: User;
}

export const UserList = ({ users, currentUser }: UserListProps) => {
  const { t } = useTranslation();

  const sortedUsers = useMemo(() => {
    return sortBy(users, ["createdAt"]);
  }, [users]);

  const isDesktop = useMediaQuery({ query: "(min-width: 1280px)" });

  const isAdminUser = isAdmin(currentUser?.role);

  const [grantAdmin] = useMutation(USER_GRANT_ADMIN, {
    refetchQueries: [{ query: USER_LIST }],
    onCompleted: ({ userGrantAdmin: { role, email } }) => {
      notification.info({
        title: t("company:usersSection.notification:permissionChanged"),
        description: t("company:usersSection.notification:changeRole", { role, email }),
      });
    },
    onError: (e) => {
      notification.danger({ title: t("company:usersSection.notification:error"), description: e.message });
    },
  });

  const [revokeAdmin] = useMutation(USER_REVOKE_ADMIN, {
    refetchQueries: [{ query: USER_LIST }],
    onCompleted: ({ userRevokeAdmin: { role, email } }) => {
      notification.info({
        title: t("company:usersSection.notification:permissionChanged"),
        description: t("company:usersSection.notification:changeRole", { role, email }),
      });
    },
    onError: (e) => {
      notification.danger({ title: t("company:usersSection.notification:error"), description: e.message });
    },
  });

  const [resendInvitation] = useMutation(USER_RESEND_INVITATION, {
    onCompleted: ({ userResendInvitation: { email } }) => {
      notification.info({
        title: t("company:usersSection.notification.userResend"),
        description: t("company:usersSection.notification:reInvited", { email }),
      });
    },
    onError: (e) => {
      notification.danger({ title: t("company:usersSection.notification:error"), description: e.message });
    },
  });

  const [deleteUser] = useMutation(USER_DELETE, {
    refetchQueries: [{ query: USER_LIST }],
    onCompleted: ({ userDelete: { email } }) => {
      notification.info({
        title: t("company:usersSection.notification.userDeleted"),
        description: t("company:usersSection.notification:deleted", { email }),
      });
    },
    onError: (e) => {
      notification.danger({ title: t("company:usersSection.notification:error"), description: e.message });
    },
  });

  const tableConfig = useMemo<TableConfig<User>>(
    () => ({
      getRowClassName: () => styles.rowWrapper,
      columns: [
        {
          header: t("company:usersSection.table.email"),
          width: 300,
          render: ({ email }) => (
            <Paragraph size="small" weight="font-bold">
              {email}
            </Paragraph>
          ),
        },
        {
          hidden: !isDesktop,
          header: t("company:usersSection.table.permission"),
          width: 100,
          render: ({ role }) => <RoleBadge role={role} />,
        },
        {
          header: t("company:usersSection.table.activatedDate"),
          width: 200,
          render: (user) => {
            const options = [
              {
                onClick: () => {
                  if (user.role === UserRole.User) {
                    return grantAdmin({
                      variables: {
                        userId: user.id,
                      },
                    });
                  }
                  return revokeAdmin({
                    variables: {
                      userId: user.id,
                    },
                  });
                },
                icon: <CrownIcon className={styles.promoteIcon} />,
                name:
                  user.role === UserRole.Admin
                    ? t("company:usersSection.table.actions.revoke")
                    : t("company:usersSection.table.actions.promote"),
              },
            ] as DropdownOption[];

            user.id !== currentUser?.id &&
              options.push({
                onClick: () => {
                  deleteUser({
                    variables: {
                      userId: user.id,
                    },
                  });
                },
                icon: <TrashCanIcon className={styles.deleteIcon} />,
                name: t("company:usersSection.table.actions.delete"),
                className: styles.deleteText,
              } as DropdownOption);

            !user.emailVerifiedAt &&
              options.push({
                onClick: () => {
                  return resendInvitation({
                    variables: {
                      userId: user.id,
                    },
                  });
                },
                className: "",
                icon: <SendIcon />,
                name: t("company:usersSection.table.actions.resend"),
              });
            return (
              <div className={styles.actionsWrapper}>
                {user.emailVerifiedAt ? (
                  <Paragraph size="small" weight="font-bold">
                    {new Date(user.emailVerifiedAt).toLocaleDateString()}
                  </Paragraph>
                ) : (
                  "-"
                )}

                {isAdminUser && (
                  <div className={styles.contextMenuWrapper}>
                    <Dropdown options={options}></Dropdown>
                  </div>
                )}
              </div>
            );
          },
        },
      ],
    }),
    [],
  );

  return (
    <div>
      <Table data={sortedUsers} config={tableConfig} />
    </div>
  );
};
